import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { WalkthroughManager } from "./WalkthroughManager";
import { WalkthroughStep } from "./types";
import { WalkthroughTooltip } from "./WalkthroughTooltip";
import "./walkthrough.css";

interface Props {
  step: WalkthroughStep;
  stepNumber: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  selectionMessage?: string | null;
}

const GAP = 14;
const PADDING = 12;
const TOOLTIP_WIDTH = 360;
const TOOLTIP_HEIGHT = 270;

export function WalkthroughOverlay(props: Props) {
  const { step } = props;
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [choiceRect, setChoiceRect] = useState<DOMRect | null>(null);
  const [pageZoom, setPageZoom] = useState(1);

  const measure = useCallback(() => {
    const target = WalkthroughManager.findTarget(step.target);
    const choiceType =
      step.id === "choose-course"
        ? "course"
        : step.id === "choose-class"
          ? "class"
          : step.id === "choose-subject"
            ? "subject"
            : step.id === "choose-chapter"
              ? "chapter"
              : null;
    const firstChoice = choiceType
      ? target?.querySelector<HTMLElement>(
          `[data-walkthrough-choice="${choiceType}"]`,
        )
      : null;
    const computedZoom = Number.parseFloat(
      window.getComputedStyle(document.body).zoom || "1",
    );
    setPageZoom(
      Number.isFinite(computedZoom) && computedZoom > 0 ? computedZoom : 1,
    );
    setRect(target?.getBoundingClientRect() ?? null);
    setChoiceRect(firstChoice?.getBoundingClientRect() ?? null);
  }, [step.id, step.target]);

  useEffect(() => {
    let cancelled = false;
    if (!step.target) {
      setRect(null);
      setChoiceRect(null);
      return;
    }
    WalkthroughManager.waitForTarget(step.target, 3000).then((target) => {
      if (cancelled) return;
      if (!target) {
        console.warn(
          `Walkthrough target not found for step "${step.id}" with selector "${step.target}". Tooltip will appear centered.`,
        );
        setRect(null);
        setChoiceRect(null);
        return;
      }
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      window.setTimeout(measure, 350);
    });
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure, step.id, step.target]);

  const position = useMemo<React.CSSProperties>(() => {
    if (!rect || step.placement === "center") {
      return { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
    }
    const viewportWidth = window.innerWidth / pageZoom;
    const viewportHeight = window.innerHeight / pageZoom;
    const normalized = {
      left: rect.left / pageZoom,
      right: rect.right / pageZoom,
      top: rect.top / pageZoom,
      bottom: rect.bottom / pageZoom,
      width: rect.width / pageZoom,
      height: rect.height / pageZoom,
    };
    const width = Math.min(TOOLTIP_WIDTH, viewportWidth - PADDING * 2);
    const maxLeft = viewportWidth - width - PADDING;
    let left = Math.min(
      maxLeft,
      Math.max(PADDING, normalized.left + normalized.width / 2 - width / 2),
    );
    let top = normalized.bottom + GAP;

    if (step.placement === "top") top = normalized.top - TOOLTIP_HEIGHT - GAP;
    if (step.placement === "left") {
      left = normalized.left - width - GAP;
      top = normalized.top + normalized.height / 2 - TOOLTIP_HEIGHT / 2;
    }
    if (step.placement === "right") {
      left = normalized.right + GAP;
      top = normalized.top + normalized.height / 2 - TOOLTIP_HEIGHT / 2;
    }
    top = Math.max(
      PADDING,
      Math.min(viewportHeight - TOOLTIP_HEIGHT - PADDING, top),
    );
    if (left < PADDING || left + width > viewportWidth - PADDING) {
      left = Math.min(
        maxLeft,
        Math.max(PADDING, normalized.left + normalized.width / 2 - width / 2),
      );
    }
    return { left, top, width };
  }, [pageZoom, rect, step.placement]);

  const highlightStyle = useMemo<React.CSSProperties | null>(() => {
    if (!rect) return null;
    const viewportWidth = window.innerWidth / pageZoom;
    const viewportHeight = window.innerHeight / pageZoom;
    const left = Math.max(6, rect.left / pageZoom);
    const top = Math.max(6, rect.top / pageZoom);
    const right = Math.min(viewportWidth - 6, rect.right / pageZoom);
    const bottom = Math.min(viewportHeight - 6, rect.bottom / pageZoom);
    return {
      left: left - 6,
      top: top - 6,
      width: Math.max(0, right - left + 12),
      height: Math.max(0, bottom - top + 12),
      pointerEvents: "none",
    };
  }, [pageZoom, rect]);

  const interactionGuards = useMemo<React.CSSProperties[]>(() => {
    if (!rect) return [];
    const viewportWidth = window.innerWidth / pageZoom;
    const viewportHeight = window.innerHeight / pageZoom;
    const left = Math.max(0, rect.left / pageZoom - 6);
    const top = Math.max(0, rect.top / pageZoom - 6);
    const right = Math.min(viewportWidth, rect.right / pageZoom + 6);
    const bottom = Math.min(viewportHeight, rect.bottom / pageZoom + 6);
    return [
      { left: 0, top: 0, width: viewportWidth, height: top },
      {
        left: 0,
        top: bottom,
        width: viewportWidth,
        height: Math.max(0, viewportHeight - bottom),
      },
      { left: 0, top, width: left, height: Math.max(0, bottom - top) },
      {
        left: right,
        top,
        width: Math.max(0, viewportWidth - right),
        height: Math.max(0, bottom - top),
      },
    ];
  }, [pageZoom, rect]);

  const handCueStyle = useMemo<React.CSSProperties | null>(() => {
    if (!step.requiresSelection || !highlightStyle) return null;
    const cueSource = choiceRect ?? rect;
    if (!cueSource) return null;
    return {
      left: cueSource.left / pageZoom + cueSource.width / pageZoom / 2,
      top: cueSource.top / pageZoom + cueSource.height / pageZoom / 2,
    };
  }, [choiceRect, highlightStyle, pageZoom, rect, step.requiresSelection]);

  return createPortal(
    <div
      className="ek-walkthrough-root"
      style={{ pointerEvents: rect ? "none" : "auto" }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        className={`ek-walkthrough-dimmer${rect ? "" : " ek-walkthrough-dimmer-full"}`}
      />
      {interactionGuards.map((guard, index) => (
        <div
          key={index}
          className="ek-walkthrough-interaction-guard"
          style={guard}
          aria-hidden="true"
        />
      ))}
      {highlightStyle && (
        <>
          <div
            className="ek-walkthrough-highlight"
            style={highlightStyle}
            aria-hidden="true"
          />
          {handCueStyle && (
            <div
              className="ek-walkthrough-hand-cue"
              style={handCueStyle}
              aria-hidden="true"
            >
              👆
            </div>
          )}
        </>
      )}
      <WalkthroughTooltip {...props} position={position} />
    </div>,
    document.body,
  );
}
